// What already answers to the same name — the backlog the new rule cannot fix.
//
// The uniqueness check in core/devices/alexaNames only refuses a NEW duplicate.
// It deliberately leaves what is already there alone, because about half of a
// real account collides and the customer did not do it: the factory defaults
// repeat on every panel, so "s1" occurs 36 times in one live account and "s2"
// 35 times. Measured, not estimated — user 1 had 226 of 435 outputs sharing a
// name, user 31 had 182 of 370.
//
// That backlog is invisible until somebody asks Alexa to turn on a light and it
// picks the wrong one, so this lists it: every name more than one thing answers
// to, and exactly where each of them is. It renders through the same report
// model as everything else (core/admin/accessReport), so it prints and exports
// with the buttons that are already built.

import { duplicateGroups } from '../devices/alexaNames';

const SR = { key: 'sr', label: 'Sr. No.', width: '8%', align: 'right' };

export const GROUP_COLUMNS = [
  SR,
  { key: 'name', label: 'Name', width: '52%' },
  { key: 'count', label: 'Answers to it', width: '20%', align: 'right' },
  { key: 'kinds', label: 'Kind', width: '20%' },
];

export const CLASH_COLUMNS = [
  SR,
  { key: 'name', label: 'Name', width: '22%' },
  { key: 'kind', label: 'Kind', width: '12%' },
  { key: 'device', label: 'Device', width: '26%' },
  { key: 'room', label: 'Room', width: '20%' },
  { key: 'button', label: 'Button', width: '20%' },
];

/** The Excel sheet — one flat filterable table, as everywhere else. */
export const EXCEL_COLUMNS = [
  { key: 'sr', label: 'Sr. No.', width: 8, type: 'number' },
  { key: 'name', label: 'Name', width: 26 },
  { key: 'kind', label: 'Kind', width: 12 },
  { key: 'device', label: 'Device', width: 28 },
  { key: 'room', label: 'Room', width: 22 },
  { key: 'button', label: 'Button', width: 14 },
];

const stamp = (at) => new Date(at)
  .toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const kindOf = (e) => (e.kind === 'device' ? 'Device' : 'Button');

/**
 * Build the report from a name index.
 *
 * ⚠️ Takes the INDEX, not a user id — so it is pure, testable without a server,
 * and cannot disagree with the check that uses the same index to refuse a
 * rename. One namespace, one definition of a clash.
 */
export function duplicateNamesReport({ index, who = '', at = Date.now() }) {
  const groups = duplicateGroups(index);
  const involved = groups.reduce((n, g) => n + g.count, 0);

  // Every clashing thing, flat — the list somebody actually works through.
  const rows = [];
  groups.forEach((g) => {
    g.entries.forEach((e) => {
      rows.push({
        sr: rows.length + 1,
        name: g.name,
        kind: kindOf(e),
        device: e.device || '—',
        room: e.room || '(no room)',
        // The key's tail is the output number for a button; a device has none.
        button: e.kind === 'button' ? String(e.key.split(':')[2]) : '—',
      });
    });
  });

  return {
    title: `Duplicate Names${who ? ` — ${who}` : ''}`,
    excelColumns: EXCEL_COLUMNS,
    meta: [
      ...(who ? [{ label: 'User', value: who }] : []),
      { label: 'Names in use twice or more', value: String(groups.length) },
      { label: 'Things involved', value: String(involved) },
      { label: 'Generated', value: stamp(at) },
    ],
    sections: [
      {
        title: 'Names shared by more than one thing',
        subtitle: 'Alexa cannot tell these apart — worst first',
        columns: GROUP_COLUMNS,
        rows: groups.map((g, i) => ({
          sr: i + 1,
          name: g.name,
          count: g.count,
          // "Device + button" is the nastiest case: a spoken name that could
          // mean either the panel or one key on it.
          kinds: [...new Set(g.entries.map(kindOf))].sort().join(' + '),
        })),
        empty: 'Every name in this account is unique. Nothing to fix.',
      },
      {
        kind: 'devices',
        title: 'Where each of them is',
        subtitle: 'Rename one of each pair to make the name unique',
        scope: { location: '', room: '' },
        columns: CLASH_COLUMNS,
        rows,
        empty: 'Nothing to list.',
      },
    ],
    total: involved,
  };
}

export default duplicateNamesReport;
